import { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useState } from "react";

import { ProjectDates, ProjectDatesData, SelectData } from "../../../../../../../../../models";
import { updateProjectDetails } from "../../../../../../../../../service/project";
import { GetWorkflowTemplateResponse, searchProjectTracker } from "../../../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../../../service/Shared";
import {
  jsonReviver,
  recursivelyValidateAndTransformDateToString,
  useIsLoadingWrapper,
} from "../../../../../../../../../utils";
import { Toast } from "../../../../../../../../../widget";

interface UseProjectApplyWorkflowTemplatePanelProps {
  workflowTemplate: GetWorkflowTemplateResponse | undefined;
  onClose: () => void;
}

interface UseProjectApplyWorkflowTemplatePanelReturnData {
  projectList: SelectData;
  getProjects: (search: string) => Promise<void>;
  errors: ServiceError[] | undefined;
  setErrors: Dispatch<SetStateAction<ServiceError[] | undefined>>;
  selectedProject: ProjectDatesData | undefined;
  setSelectedProject: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
  showPermissionsPanel: boolean;
  setShowPermissionsPanel: Dispatch<SetStateAction<boolean>>;
  isHandleSubmitProjectPhaseDatesLoading: boolean;
  handleClearSelection: () => void;
  handleSubmitProjectPhaseDates: () => Promise<void>;
  handleSubmitProjectPhaseDatesForm: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleClose: () => void;
}

export const useProjectApplyWorkflowTemplatePanel = ({
  workflowTemplate,
  onClose,
}: UseProjectApplyWorkflowTemplatePanelProps): UseProjectApplyWorkflowTemplatePanelReturnData => {
  const [errors, setErrors] = useState<ServiceError[] | undefined>([]);
  const [isHandleSubmitProjectPhaseDatesLoading, setIsHandleSubmitProjectPhaseDatesLoading] = useState(false);
  const [showPermissionsPanel, setShowPermissionsPanel] = useState(false);
  const [projectList, setProjectList] = useState<SelectData>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectDatesData>();

  const handleClearSelection = useCallback((): void => {
    setShowPermissionsPanel(false);
    setErrors([]);
    setSelectedProject(undefined);
  }, []);

  const handleClose = (): void => {
    handleClearSelection();
    onClose();
  };

  const getProjects = useCallback(
    async (search: string) => {
      if (workflowTemplate?.projectType?.uuid) {
        const response = await searchProjectTracker({
          paging: {
            limit: 20,
            beforeCursor: "",
            afterCursor: "",
          },
          sort: [
            {
              key: "displayName" as "results.displayName",
              direction: "asc",
            },
          ],
          filter: {
            results: {
              projectType: {
                uuid: {
                  operator: "eq",
                  value: workflowTemplate.projectType.uuid,
                },
              },
              displayName: {
                operator: "startsWith",
                value: search,
              },
              workflow: {
                uuid: {
                  operator: "eq",
                  value: null,
                },
              },
            },
          },
        });

        if (response.status === Status.Success && response.data?.results) {
          const list = response.data.results.flatMap((result) => {
            if (result.uuid && result.displayName) {
              return { key: result.uuid, value: result.displayName };
            }

            return [];
          });

          setProjectList(list);
        }
      }
    },
    [workflowTemplate?.projectType?.uuid]
  );

  const handleSubmitProjectPhaseDates = useIsLoadingWrapper(async (): Promise<void> => {
    if (selectedProject === undefined) {
      Toast.warning({ message: "Please select a project" });
      return;
    }
    setErrors(undefined);

    const datesDeepCopy = JSON.parse(JSON.stringify(selectedProject.dates), jsonReviver) as ProjectDates;
    const transformedDates = recursivelyValidateAndTransformDateToString(datesDeepCopy);

    if (transformedDates.errors.length > 0) {
      setErrors(transformedDates.errors);
      return;
    }

    const updateProjectDetailsRes = await updateProjectDetails({
      projectUuid: selectedProject.uuid,
      dates: JSON.stringify(transformedDates.data),
      rowVersion: selectedProject.rowVersion,
      addressCountryCode: null,
      displayName: null,
      locationCoordinates: null,
      locationGridReference: null,
      areaNetHa: null,
      validatorUuid: null,
      name: null,
      externalReference: null,
      addressRegion: null,
    });

    if (updateProjectDetailsRes.status === Status.Success && updateProjectDetailsRes.data) {
      setShowPermissionsPanel(true);
      setSelectedProject({
        ...selectedProject,
        rowVersion: updateProjectDetailsRes.data.rowVersion,
      });
    }

    if (updateProjectDetailsRes.status === Status.Error) {
      setErrors(updateProjectDetailsRes.errors);
    }
  }, setIsHandleSubmitProjectPhaseDatesLoading);

  const handleSubmitProjectPhaseDatesForm = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    handleSubmitProjectPhaseDates();
  };

  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      try {
        await getProjects("");
      } catch (error) {
        Toast.error({ message: error instanceof Error ? error.message : "Unable to load projects" });
      }
    };

    loadProjects();
  }, [getProjects]);

  return {
    projectList,
    getProjects,
    errors,
    setErrors,
    selectedProject,
    setSelectedProject,
    showPermissionsPanel,
    setShowPermissionsPanel,
    isHandleSubmitProjectPhaseDatesLoading,
    handleClearSelection,
    handleSubmitProjectPhaseDates,
    handleSubmitProjectPhaseDatesForm,
    handleClose,
  };
};
